import { Pause, Play, Upload } from '@carbon/icons-react';
import { useTheme } from '@repo/theme';
import { HorizBox, Pad } from 'component/basics';
import { ActionButton, CTAButton, TextButton } from 'component/button';
import { Paragraph, UtilityText } from 'component/text';
import { useVideo, VideoPlayerStateContext } from 'open-modules/zdf/component/videoplayer/videoplayer-state';
import { PercentageBar, PercentageBarContainer } from 'open-modules/zdf/videovoting/videovotingPercentageBars';
import React, { useState } from 'react';
import { View } from 'react-native';
import { getPrettyTimestamp } from 'system/videotime';
import { t } from 'util/stdtypes';

import type { VideoPlayerState } from './videoplayer-types';

// Fake video hook
type FakeVideoPlayerState = VideoPlayerState & { loadVideo: () => void };
function useFakeVideoPlayer(initialState: VideoPlayerState | undefined): FakeVideoPlayerState {
    const [paused, setPaused] = useState(initialState?.paused ?? false);
    const [ready, setReady] = useState(initialState?.ready ?? false);
    const [currentTime, setCurrentTime] = useState(initialState?.currentTime ?? 0);
    const duration = initialState?.duration ?? 600;
    const ended = initialState?.ended ?? false;

    return {
        paused,
        ready,
        currentTime,
        duration,
        ended,
        src: '',
        play: () => {
            setPaused(false);
        },
        pause: () => setPaused(true),
        seek: (time) => setCurrentTime(time),
        loadVideo: () => setReady(true),
    };
}

// Helper component to make any video based component demoable without deep
// video integration It contains a fake video UI und prints the video voting
// session state
type DemoVideoPlayerContainerProps = {
    children: React.ReactNode;
    videoState?: VideoPlayerState;
};
export function DemoVideoPlayerContainer({ children, videoState }: DemoVideoPlayerContainerProps) {
    const fakeVideoState = useFakeVideoPlayer(videoState);
    const { colors } = useTheme();

    return (
        <VideoPlayerStateContext.Provider value={fakeVideoState}>
            <View style={{ position: 'relative', width: '100%', height: 700, backgroundColor: colors.colorSurfaceSecondary }}>
                {children}
                <View style={{ bottom: 0, position: 'absolute', width: '100%' }}>
                    <VideoBar />
                </View>
            </View>
        </VideoPlayerStateContext.Provider>
    );
}

function VideoBar() {
    const video = useVideo() as FakeVideoPlayerState;
    const { colors } = useTheme();

    const seekVideo = (amount: number) => {
        let newTime = video.currentTime + amount;
        newTime = Math.min(video.duration, Math.max(newTime, 0));
        video?.seek?.(newTime);
    };

    return (
        <View style={{ flex: 1, flexGrow: 1 }}>
            {!video.ready ? (
                <TextButton label={t('demo.load_video_element', 'Load video element')} leftIcon={Upload} onPress={video.loadVideo} />
            ) : (
                <View>
                    <PercentageBarContainer backgroundColor={colors.colorButtonDisabled} thickness={10}>
                        <PercentageBar colors={[colors.colorTextSuccess]} percentages={[video.currentTime]} totalValue={video.duration} />
                    </PercentageBarContainer>
                    <HorizBox center spread>
                        <View>
                            {!video.paused ? (
                                <ActionButton icon={Pause} label={t('demo.pause', 'PAUSE')} onPress={video.pause} />
                            ) : (
                                <ActionButton icon={Play} label={t('demo.play', 'PLAY')} onPress={video.play} />
                            )}
                        </View>
                        <HorizBox>
                            <TextButton
                                label={t('demo.prev', 'PREV')}
                                onPress={() => {
                                    seekVideo(-60);
                                }}
                            />
                            <Pad />
                            <TextButton
                                label={t('demo.fwrd', 'FWRD')}
                                onPress={() => {
                                    seekVideo(60);
                                }}
                            />
                        </HorizBox>
                        <View style={{ paddingHorizontal: 8 }}>
                            <UtilityText
                                text={getPrettyTimestamp(video.currentTime, true) + '/' + getPrettyTimestamp(video.duration, true)}
                            />
                        </View>
                    </HorizBox>
                </View>
            )}
        </View>
    );
}
export function SimpleVideoControls() {
    const video = useVideo();
    return (
        <View>
            <HorizBox>
                <CTAButton label={t('videoplayer_demo.play', 'PLAY')} onPress={video?.play}></CTAButton>
                <Pad size={10} />
                <CTAButton label={t('videoplayer_demo.pause', 'PAUSE')} onPress={video?.pause}></CTAButton>
                <Pad size={10} />
                <CTAButton label={t('videoplayer_demo.seek_60', 'SEEK TO 60 SECONDS')} onPress={() => video?.seek?.(60)}></CTAButton>
            </HorizBox>
            <Pad size={10} />
            <Paragraph text={JSON.stringify(video, null, ' ')} />
        </View>
    );
}
